// No persona preamble ("You are an expert...") - current models follow the goal
// itself; what matters is saying exactly what to produce.
export const BASE_PROMPT_PREFIX = `Create a complete, self-contained single-page HTML app (this can be a game, tool, or utility).\n`;

// Output instructions. The persistence hint is only added for apps/utilities —
// adding it to games is noise that can steer behaviour unhelpfully.
const OUTPUT_INSTRUCTION = `Please output a complete, self-contained HTML file with CSS and JavaScript that I can run immediately.`;
const PERSISTENCE_INSTRUCTION = `If the app needs to remember data between visits, persist it in the browser's localStorage.`;
const COMMENT_INSTRUCTION = `Make sure to include comments explaining how the code works.`;

export const buildPromptFromDetails = (
    details: WizardDataState,
    kind: TemplateKind = 'game'
): string => {
    if (
        !details.gameName &&
        !details.objective &&
        !details.coreMechanics &&
        !details.visualStyle &&
        !details.keyElements
    ) {
        return '';
    }
    const suffix = [
        OUTPUT_INSTRUCTION,
        kind === 'app' ? PERSISTENCE_INSTRUCTION : '',
        COMMENT_INSTRUCTION
    ]
        .filter(Boolean)
        .join(' ');
    return `${BASE_PROMPT_PREFIX}
Name: ${details.gameName || '[Specify Name]'}
Objective: ${details.objective || '[Specify Objective]'}
Core Mechanics: ${details.coreMechanics || '[Specify Core Mechanics]'}
Visual Style: ${details.visualStyle || '[Specify Visual Style]'}
Key elements to include: ${details.keyElements || '[Specify Key Elements]'}

${suffix}`;
};

export const initialWizardState = {
    gameName: '',
    objective: '',
    coreMechanics: '',
    visualStyle: '',
    keyElements: ''
};
export type WizardDataState = typeof initialWizardState;
export type WizardField = keyof WizardDataState;

// A template (and the builder ideas/wording) can describe either a game or a
// practical app/utility.
export type TemplateKind = 'game' | 'app';

export const randomDataSources: Record<
    TemplateKind,
    Record<WizardField, string[]>
> = {
    game: {
        gameName: [
            'Pixel Jumper',
            'Galaxy Quest',
            'Math Mania',
            'Word Weaver',
            'Color Splash',
            'Dungeon Crawler',
            'Rhythm Runner',
            'Tower Defender',
            'Card Battler',
            'Stealth Agent'
        ],
        objective: [
            'Reach the highest score',
            'Collect all items',
            'Survive for 5 minutes',
            'Solve the puzzle',
            'Defeat the final boss',
            'Escape the labyrinth',
            'Build the tallest structure',
            'Complete all quests',
            'Unlock all characters'
        ],
        coreMechanics: [
            'Platform jumping & collectibles',
            'Tile matching puzzle',
            'Top-down enemy shooting',
            'Resource gathering & crafting',
            'Quick time event challenges',
            'Card-based combat system',
            'Stealth and evasion mechanics',
            'Physics-based puzzles',
            'Dialogue choices impacting story'
        ],
        visualStyle: [
            '8-bit retro pixel art',
            'Minimalist flat design',
            'Neon cyberpunk aesthetic',
            'Bright and colorful cartoon style',
            'Dark and moody fantasy theme',
            'Watercolor painting effect',
            'Low-poly 3D graphics',
            'Hand-drawn sketch look',
            'Sci-fi futuristic interface'
        ],
        keyElements: [
            'Timer and high score display',
            'Lives and health bar system',
            'Unlockable power-ups',
            'Multiple progressively harder levels',
            'Character selection screen',
            'Inventory system',
            'Achievements list',
            'Sound effects and background music toggle',
            'Tutorial section',
            'Pause menu',
            'Leaderboard'
        ]
    },
    app: {
        gameName: [
            'Focus Timer',
            'Daily Tracker',
            'Quick Notes',
            'Budget Buddy',
            'Task Flow',
            'Habit Hero',
            'Study Log',
            'Penny Wise',
            'List Master',
            'Day Blocks'
        ],
        objective: [
            'Track daily tasks and check them off',
            'Stay focused with timed work sessions',
            'Log expenses and see where money goes',
            'Build habits with daily streaks',
            'Save and organise quick notes',
            'Plan the day hour by hour',
            'Convert between units instantly',
            'Keep a running shopping list',
            'Review progress over time'
        ],
        coreMechanics: [
            'Add, edit, complete and delete items',
            'Drag items between columns',
            'Start, pause and reset a timer',
            'Tap to check off each day',
            'Enter values and see live results',
            'Filter and sort the list',
            'Pick a category and log an entry',
            'Swipe or click to remove entries'
        ],
        visualStyle: [
            'Clean minimal interface',
            'Dark mode friendly',
            'Colourful card layout',
            'Soft pastel theme',
            'Modern flat design with rounded corners',
            'Spacious distraction-free layout',
            'Bold, large-tap-target mobile layout'
        ],
        keyElements: [
            'Saves data to localStorage so it persists',
            'Categories and tags',
            'Running totals and summary',
            'Progress bar or ring',
            'Search and filter',
            'Edit and delete entries',
            'Daily and weekly history view',
            'Streak counter',
            'Export data as JSON',
            'Helpful empty-state hints'
        ]
    }
};
